import { useEffect, useState, useMemo } from 'react';
import { Stack, Title, TextInput, Loader, Center, Text, Pagination, Select, Group } from '@mantine/core';
import { Search } from 'lucide-react';
import { useStore } from '../store/useStore';
import { PersonaTable } from '../components/PersonaTable';

export function Personas() {
  const fetchPersonas = useStore((state) => state.fetchPersonas);
  const personas = useStore((state) => state.personas);
  const personasLoading = useStore((state) => state.personasLoading);
  const personasPage = useStore((state) => state.personasPage);
  const personasTotalCount = useStore((state) => state.personasTotalCount);
  const personasPageSize = useStore((state) => state.personasPageSize);
  const setPersonasPage = useStore((state) => state.setPersonasPage);
  const setPersonasPageSize = useStore((state) => state.setPersonasPageSize);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPersonas();
  }, [personasPage, personasPageSize, fetchPersonas]);

  const handlePageSizeChange = (value: string | null) => {
    if (value) {
      setPersonasPageSize(parseInt(value));
      setPersonasPage(1);
    }
  };

  const filteredPersonas = useMemo(() => {
    if (!searchQuery.trim()) return personas;

    const query = searchQuery.toLowerCase();
    return personas.filter((persona) =>
      persona.persona_id.toLowerCase().includes(query) ||
      persona.status?.toLowerCase().includes(query)
    );
  }, [personas, searchQuery]);

  return (
    <Stack gap="lg">
      <Title order={1}>Personas</Title>

      <TextInput
        placeholder="Search by persona ID or status..."
        leftSection={<Search size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
      />

      {personasLoading ? (
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      ) : filteredPersonas.length > 0 ? (
        <>
          <PersonaTable personas={filteredPersonas} />
          <Group justify="space-between" mt="md">
            <Pagination
              total={Math.ceil(personasTotalCount / personasPageSize)}
              value={personasPage}
              onChange={setPersonasPage}
            />
            <Group gap="xs">
              <Text size="sm">Per page:</Text>
              <Select
                value={personasPageSize.toString()}
                onChange={handlePageSizeChange}
                data={['10', '20', '50', '100']}
                w={80}
              />
            </Group>
          </Group>
        </>
      ) : searchQuery ? (
        <Center h={400}>
          <Text c="dimmed">No personas match your search</Text>
        </Center>
      ) : (
        <Center h={400}>
          <Text c="dimmed">No personas found</Text>
        </Center>
      )}
    </Stack>
  );
}
